import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, createNgModule, Injector, NgModuleRef, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { debounceTime, Observable } from 'rxjs';
import { CloseButtonService } from './services/close-button.service';
import { AsyncPipe } from '@angular/common';
import { LoadingComponent } from './components/core/loading/loading.component';
import { GameComponent } from './components/core/game/game.component';
import { OnboardingComponent, OnboardingModalType } from './components/ui/overlays/onboarding/onboarding.component';
import { GameUiService } from './services/game-ui/game-ui.service';
import { GameState } from './services/game-ui/game-state.enum';
import { GameStateService } from './services/game-state/game-state.service';
import { GenderSelectComponent } from './components/ui/overlays/gender-select/gender-select.component';
import { QuestionComponent } from './components/ui/modals/question/question.component';
import { ResumeComponent } from './components/ui/modals/resume/resume.component';
import { NextStepsComponent } from './components/ui/modals/next-steps/next-steps.component';
import { SignialsService } from './services/signals/signials.service';
import { NpsDetailComponent } from './components/core/nps-detail/nps-detail.component';
import { FinishModalComponent } from './components/ui/modals/finish-modal/finish-modal.component';
import { FeedbackComponent } from './components/ui/modals/feedback/feedback.component';
import { SPINE_KEYS } from './constants/textures';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { isMobile } from './classes/common/helpers';
import { Graphics } from 'pixi.js';
import { HowToPlayComponent } from './components/ui/overlays/how-to-play/how-to-play.component';
import { MetrikaService } from './services/metrika.service';
const attrToMetrika = {
  freid: 'personality',
  witch: 'fear',
  mirrors: 'myths',
  rider: 'climbing',
  koldun: 'darts',
  mag: 'guess_profession',
  robin: 'archery',
  koldun2: 'mix_darts',
  strongman: 'rope'
}
// Интерфейс для лениво загружаемых компонентов
interface LazyComponent {
  name: string;
  component: Type<any>;
}

const modalInfo = {
  // 'freid','witch','mirrors','rider','koldun','mag','robin','koldun2','strongman'
  freid: {
    title: 'Тип личности',
    description: 'Определи свой тип личности, сильные стороны и то, что тебе действительно интересно.',
  },
  witch: {
    title: 'Страхи выбора',
    description: 'Разберись со страхом, который влияет на выбор профессии, и пойми, как с ним справиться.',
  },
  mirrors: {
    title: 'Мифы о профессиях',
    description: 'Разберись с мифами о профессиях и узнай, что на самом деле важно при выборе.',
  },
  rider: {
    title: 'Мотивация',
    description: 'Определи, что даёт тебе силы двигаться к цели и сохранять мотивацию.',
  },
  koldun: {
    title: 'Выбор интереса',
    description: 'Выбери занятия, которые тебе интересны, и найди подходящую сферу.',
  },
  mag: {
    title: 'Угадай профессию',
    description: 'Проверь, насколько хорошо ты ориентируешься в профессиях из выбранной сферы.',
  },
  robin: {
    title: 'Выбор профессии',
    description: 'Посмотри на профессии через их реальные задачи и выбери ту, что ближе тебе.',
  },
  koldun2: {
    title: 'Микс интересов',
    description: 'Соедини разные интересы и посмотри необычные варианты профессий.',
  },
  strongman: {
    title: 'Поддержка близких',
    description: 'Подготовь ответ близким	Научись отвечать тем, кто сомневается в твоём выборе профессии.',
  }
}
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
  isLoading = true;

  modalInfo = modalInfo;
  private featureModuleRef: NgModuleRef<any> | null = null;
  private lazyComponents = new Map<string, Type<any>>(); // Кэш классов компонентов

  infoModalVisible = false;
  onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.doSomethingOnLeave(); // пользователь ушел с вкладки
    } else if (document.visibilityState === 'visible') {
      this.doSomethingOnReturn(); // пользователь вернулся
    }
  }
  
  onBeforeUnload = (event: BeforeUnloadEvent): void => {
    this.doSomethingOnLeave();
  }

  currentType: string | undefined;
  constructor(
    private cdr: ChangeDetectorRef,
    private signals: SignialsService,
    private state: GameStateService,
    private injector: Injector,
    private metrika: MetrikaService,
  ) {
    this.signals.modalShow.pipe(debounceTime(100)).subscribe(async (type) => {
      this.container?.clear?.();
      switch (type) {
        case 'question':
          await this.loadQuestionComponent();
          break;
        case 'feedback':
          await this.loadFeedbackComponent();
          break;
        case 'resume':
          await this.loadResumeComponent();
          break;
        case 'next-steps':
          await this.loadNextStepsComponent();
          break;
        case 'finish-modal':
          await this.loadFinishModalComponent();
          break;
        case 'onboarding-start':
          await this.loadOnboardingComponent(0);
          break;
        case 'onbording-trap':
          await this.loadOnboardingComponent(1);
          break;
        case 'select-gender':
          await this.loadSelectGenderComponent();
          break;
        default:
          await this.showInfoModal(type);
          break;
      }
    });
    window.addEventListener('wind_click', () => {
      this.metrika.reachGoal('wind_click');
    });
    window.addEventListener('first_shield_received', () => {
      this.metrika.reachGoal('first_shield_received');
    });
  }

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }
  
  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    if (this.featureModuleRef) {
      this.featureModuleRef.destroy();
    }
  }

  private doSomethingOnLeave(): void {
    // this.sound.pauseAll();
  }

  private doSomethingOnReturn(): void {
    // this.sound.resumeAll();
  }

  /**
   * Предзагрузка feature модуля для более быстрого отображения модалок
   */
  private async preloadFeatureModule(): Promise<void> {
    // Загружаем модуль в фоне после инициализации основного приложения
    setTimeout(async () => {
      try {
        await this.loadFeatureModule();
        // console.log('FeatureModule предзагружен');
      } catch (error) {
        console.error('Ошибка предзагрузки FeatureModule:', error);
      }
    }, 1000);
  }
  
  /**
   * Загрузка feature модуля (ленивая загрузка)
   */
  private async loadFeatureModule(): Promise<void> {
    if (this.featureModuleRef) {
      return; // Модуль уже загружен
    }

    try {
      // Динамический импорт feature модуля
      const { FeatureModule } = await import('./feature.module');
      
      // Создаем экземпляр модуля с помощью createNgModule
      this.featureModuleRef = createNgModule(FeatureModule, this.injector);
      
    } catch (error) {
      console.error('Ошибка загрузки FeatureModule:', error);
      throw error;
    }
  }

  async loadResumeComponent() {
    this.state.gamePause = true;
    const componentRef = this.container.createComponent(ResumeComponent);
    const subs = componentRef.instance.close.subscribe(() => {
      localStorage.setItem('resume', 'shown');
      componentRef.destroy();
      this.container.clear();
      this.state.gamePause = false;
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
    });
    componentRef.changeDetectorRef.detectChanges();
  }

  async loadNextStepsComponent() {
    // Загружаем feature модуль, если еще не загружен
    await this.loadFeatureModule();
    
    const componentRef = this.container.createComponent(
      NextStepsComponent,
      { ngModuleRef: this.featureModuleRef! }
    );
    
    const subs = componentRef.instance.close.subscribe(() => {
      componentRef.destroy();
      this.container.clear();
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
    });
    componentRef.changeDetectorRef.detectChanges();
  }

  async loadFinishModalComponent() {
    // Загружаем feature модуль, если еще не загружен
    await this.loadFeatureModule();
    
    // Динамически импортируем компонент из feature.module
    const componentRef = this.container.createComponent(
      FinishModalComponent,
      { ngModuleRef: this.featureModuleRef! }
    );
    
    const subs = componentRef.instance.close.subscribe(() => {
      componentRef.destroy();
      this.container.clear();
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
    });
    componentRef.changeDetectorRef.detectChanges();
  }
  
  async loadOnboardingComponent(type: OnboardingModalType) {
    if (type === 1) {
      // OnboardingComponent
        const componentRef = this.container.createComponent(OnboardingComponent); // ← уже импортирован!
        componentRef.instance.modalType = type;
        const subs = componentRef.instance.close.subscribe(() => {
          componentRef.destroy();
          this.container.clear();
          this.signals.modalClosed.next(true);
          subs.unsubscribe();
        });
        componentRef.changeDetectorRef.detectChanges();
    } else {

      const componentRef = this.container.createComponent(HowToPlayComponent); // ← уже импортирован!
      setTimeout(() => {
        const id = document.getElementById('howToPlay');
        if (id) {
          id.classList.add('how-to-play-visible');
        }
      }, 100);
      componentRef.instance.modalType = type;
      const subs = componentRef.instance.close.subscribe(() => {
        componentRef.destroy();
        this.container.clear();
        this.signals.modalClosed.next(true);
        subs.unsubscribe();
      });
      componentRef.changeDetectorRef.detectChanges();
    }
  }

  async loadSelectGenderComponent() {
    const componentRef = this.container.createComponent(GenderSelectComponent); // ← уже импортирован!
    const subs = componentRef.instance.close.subscribe((gender) => {
      localStorage.setItem('gender', gender);
      componentRef.destroy();
      this.container.clear();
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
    });
    componentRef.changeDetectorRef.detectChanges();
  }

  async loadQuestionComponent() {
    const componentRef = this.container.createComponent(QuestionComponent);
    const subs = componentRef.instance.closeModal.subscribe(() => {
      componentRef.destroy();
      this.container.clear();
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
    });
    componentRef.changeDetectorRef.detectChanges();
  }
  
  async loadFeedbackComponent() {
    // Загружаем feature модуль, если еще не загружен
    await this.loadFeatureModule();
    
    // Динамически импортируем компонент из feature.module
    const componentRef = this.container.createComponent(
      FeedbackComponent,
      { ngModuleRef: this.featureModuleRef! }
    );
    
    const subs = componentRef.instance.closeModal.subscribe(() => {
      componentRef.destroy();
      this.container.clear();
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
    });
    componentRef.changeDetectorRef.detectChanges();
  }

  initGame() {
    this.isLoading = false;
    this.cdr.detectChanges();
    this.preloadFeatureModule();
  }

  showInfoModal(type: string) {

    const attractionStatus = this.state.getAttractionStatus(type === 'strongman'? 'silach' : type === 'koldun' ? 'koldunGrey' : type ==='mag' ? 'magician' : type === 'koldun2' ? 'koldunPurple' : type);
    
    this.metrika.reachGoal('attraction_start', {attraction: attrToMetrika[type]});
    if (['freid','witch','mirrors','rider','koldun','mag','robin','koldun2','strongman'].includes(type) && attractionStatus?.result) {
      this.loadComponent(type);
    } else if (['eddy', 'tubeEddy', 'startEddy', 'thirdEddy', 'finalEddy', 'final2Eddy'].includes(type)) {
      this.loadComponent(type);
    } else {
      this.currentType = type;
      this.infoModalVisible = true;
      this.cdr.detectChanges();
    }
  }
  async openGameModal() {
    this.currentType && await this.loadComponent(this.currentType);
    this.currentType = undefined;
  }

  closeInfoModal() {
    this.currentType = undefined;
    this.infoModalVisible =false;
    this.cdr.detectChanges();
    this.state.gamePause = false;
  }
  async loadComponent(type: string) {
    this.state.progressBar?.hide();
    const componentRef = this.container.createComponent(NpsDetailComponent);
    componentRef.instance.detailKey = type;
    const subs = componentRef.instance.closed.subscribe(() => {
      this.state.progressBar?.show();
      componentRef.destroy();
      this.container.clear();
      // console.log('CLOSED DETAIL');
      this.signals.modalClosed.next(true);
      subs.unsubscribe();
      const f = {
        'freid': 1,
        'witch': 2,
        'mirrors': 3,
        'rider': 4,
        'koldun': 5,
        'mag': 6,
        'robin': 7,
        'koldun2': 8,
        'strongman': 9
       } as Record<string, number>;
      const index = f[type];
      const attractionStatus = this.state.getAttractionStatus(type === 'strongman'? 'silach' : type === 'koldun' ? 'koldunGrey' : type ==='mag' ? 'magician' : type === 'koldun2' ? 'koldunPurple' : type);
      // console.log(attractionStatus, type);
      if (['freid','witch','mirrors','rider','koldun','mag','robin','koldun2','strongman'].includes(type) && attractionStatus?.result) {
        this.metrika.reachGoal('attraction_complete', {attraction: attrToMetrika[type]});
        const texture = this.state.resourses.getSpineData(SPINE_KEYS.uiReward);
        if (texture) {
          this.state.sound.playSfx('star_appears');
          setTimeout(() => {this.state.sound.playSfx('star_dust')}, 1050);
          const overlay = new Graphics();
          overlay.rect(0, 0, window.innerWidth, window.innerHeight);
          overlay.fill({ color: 0x000000, alpha: 0.3 });  
          const spine = Spine.from({skeleton:`${SPINE_KEYS.uiReward}_skeleton`, atlas: `${SPINE_KEYS.uiReward}_atlas`});
          // {skeleton:`${key}_skeleton`, atlas: `${key}_atlas`}
          spine.state.setAnimation(0, 'fly', true);
          const mult = isMobile() ? 0.5 : 1;
          spine.scale.x = spine.scale.y = mult;
          const bone = spine.skeleton.findBone('END');
          spine.x = window.innerWidth /2;
          spine.y = window.innerHeight / 2;
          if (bone) {
            bone['x'] = -window.innerWidth/(2*mult) + 16 + (window.innerWidth < 768 ? 50 : 45);
            bone['y'] = window.innerHeight/(2*mult) - 16 - (window.innerWidth < 768 ? 50 : 45);
          }
          spine.eventMode = 'none';
          this.state.containers.camera.addChild(overlay,spine);

          // setTimeout(() => {
          // const resume = document.getElementById('resume') as HTMLButtonElement;
            // resume && resume.classList.add('bounds');
            // this.cdr.detectChanges();
          // }, 2000);
          setTimeout(() => {
            // const resume = document.getElementById('resume') as HTMLButtonElement;
            // resume && resume.classList.remove('bounds');
            this.state.signal.updateSegments$.next(true);
            this.state.containers.camera.removeChild(overlay,spine);
            this.state.gamePause = false;
            this.cdr.detectChanges();
            this.state.progressBar.playAction('star', index);
            const segments = this.state.getCompletedAttractionsCount();
            if (segments === 9) {
              for (let i = 0; i <= 9; i++) {
                setTimeout(() => {
                  this.state.progressBar.playAction('star', i);
                  if (i === 0) this.state.sound.playSfx('star_appears');
                }, i * 100 + 200);

              }
            }
          }, 2500);

        }

      }
      this.state.sound.playFon();
    });
    componentRef.changeDetectorRef.detectChanges();
  }
}